import {
  Banknote,
  Boxes,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  Package,
  Percent,
  TrendingDown,
  Truck,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

/**
 * Content for the Black Friday tools hub (/tools).
 *
 * The hub presents four ordered stages — plan → price → fund → review — as the
 * typical journey a merchant runs through, with the tools that live under each.
 * The individual tools are not built yet; while a tool is a placeholder its
 * `href` is `null` and it renders with a "Coming soon" label instead of a link.
 */

export interface ToolEntry {
  title: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Destination once the tool is built. `null` while the tool is a placeholder. */
  href: string | null;
  /** Optional one-line description shown under the title for live tools. */
  subtitle?: string;
}

export interface ToolCategory {
  /** Anchor id + deep-link target for the stage. */
  id: string;
  label: string;
  tools: ToolEntry[];
}

export const HERO = {
  eyebrow: "Black Friday toolkit",
  title: "Black Friday toolkit for eCommerce brands",
  subtitle:
    "Free tools to plan, price, fund and review your Black Friday weekend.",
  subscribeInsightsTitle: "Get more Wayflyer Insights in your inbox",
  subscribeInsightsSubtitle:
    "Subscribe for data-driven tips to grow your eCommerce brand.",
  subscribeInsightsLabel: "Subscribe",
  subscribeInsightsSuccess:
    "Thanks, you're subscribed. Look out for Wayflyer Insights in your inbox.",
  cta: { label: "Explore the tools", href: "#toolkit" },
} as const;

export const CATEGORIES: ToolCategory[] = [
  {
    id: "plan",
    label: "Plan",
    tools: [
      {
        title: "Readiness checklist",
        icon: ClipboardList,
        href: "/tools/readiness-scorecard",
        subtitle:
          "20 yes/no checks to see how ready you are, and what to fix first",
      },
      {
        title: "Backward planner",
        icon: CalendarClock,
        href: "/tools/backwards-planner",
        subtitle: "Work back from your sale date to see when each task is due",
      },
    ],
  },
  {
    id: "price",
    label: "Price",
    tools: [
      {
        title: "Discount break-even calculator",
        icon: Percent,
        href: "/tools/discount-break-even",
        subtitle: "How many more units a discount needs to hold profit",
      },
      {
        title: "Free-shipping threshold calculator",
        icon: Truck,
        href: "/tools/free-shipping-threshold",
        subtitle: "The basket size that makes free shipping pay for itself",
      },
      {
        title: "Bundle pricing calculator",
        icon: Package,
        href: "/tools/bundle-pricing",
        subtitle: "The deepest bundle discount that still holds your margin",
      },
    ],
  },
  {
    id: "fund",
    label: "Fund",
    tools: [
      {
        title: "Financing break-even",
        icon: Banknote,
        href: "/tools/financing-break-even",
        subtitle:
          "The share of extra stock you must sell for financing to pay off",
      },
      {
        title: "Order quantity calculator",
        icon: Boxes,
        href: "/tools/order-quantity-calculator",
        subtitle: "How many units to order without over- or under-buying",
      },
    ],
  },
  {
    id: "review",
    label: "Review",
    tools: [
      {
        title: "Black Friday report card",
        icon: ClipboardCheck,
        href: "/tools/bf-report-card",
        subtitle: "Grade last year's weekend and see what to fix next time",
      },
      {
        title: "Clearance ladder",
        icon: TrendingDown,
        href: "/tools/clearance-ladder",
        subtitle: "A markdown schedule for the stock Black Friday left behind",
      },
    ],
  },
];

export const APPLY_CTA = {
  title: "Fund your biggest Black Friday yet",
  cta: {
    label: "Apply for funding",
    href: "https://app.wayflyer.com/authn/signup",
  },
} as const;

export const SUBSCRIBE = {
  title: "Get more Wayflyer Insights in your inbox",
} as const;

/** Top 4 tools by PostHog pageviews (last 90 days) for the nav Tools preview. */
export const TOP_NAV_TOOLS: readonly { title: string; href: string }[] = [
  {
    title: "Offer visualisation",
    href: "/tools/offer-calculator",
  },
  {
    title: "Financing break-even",
    href: "/tools/financing-break-even",
  },
  {
    title: "Bundle pricing calculator",
    href: "/tools/bundle-pricing",
  },
  {
    title: "Discount break-even calculator",
    href: "/tools/discount-break-even",
  },
] as const;
